import { useCallback, useRef, useState } from "react";
import { Banner, ContentModal } from "@/shared/ui";
import { useAuth } from "@/shared/auth/AuthContext";
import { usePrintRender } from "@/modules/print/hooks/usePrintRender";
import { exportPdfErrorMessage, exportPrintPdf } from "@/modules/print/lib/exportPrintPdf";
import { printHtmlDocument } from "@/platform/printDocument";

export type PrintPreviewTarget = {
  docCode: string;
  entityKey: string;
  entityType?: string;
  title?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: PrintPreviewTarget | null;
};

export function PrintPreviewDialog({ open, onOpenChange, target }: Props) {
  const { http } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const docCode = target?.docCode ?? "";
  const entityKey = target?.entityKey ?? "";
  const entityType = target?.entityType;
  const title = target?.title ?? (entityKey ? `${docCode} · ${entityKey}` : "Xem trước in");

  const render = usePrintRender(
    open && docCode && entityKey
      ? { docCode, entityKey, entityType, format: "html", archive: false }
      : null,
  );

  const printDocument = useCallback(() => {
    const html = render.data?.html;
    if (html) {
      void printHtmlDocument(html, title);
      return;
    }
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
      return;
    }
    window.print();
  }, [render.data?.html, title]);

  const exportPdf = useCallback(() => {
    if (!docCode || !entityKey) return;
    setExportError(null);
    void exportPrintPdf(http, { docCode, entityKey, entityType }).catch((e) => {
      setExportError(exportPdfErrorMessage(e));
    });
  }, [http, docCode, entityKey, entityType]);

  const canAct = Boolean(render.data?.html);

  return (
    <ContentModal
      open={open}
      onOpenChange={(next) => {
        if (!next) setExportError(null);
        onOpenChange(next);
      }}
      title={title}
      description={entityKey ? `Chứng từ ${entityKey}` : undefined}
      lockOutsideClose
      actions={[
        {
          id: "export-pdf",
          label: "Xuất PDF",
          onClick: exportPdf,
          disabled: !canAct,
        },
      ]}
      primaryAction={{
        id: "print",
        label: "In",
        onClick: printDocument,
        disabled: !canAct,
      }}
    >
      <div className="space-y-3">
        {exportError ? <Banner tone="danger" title={exportError} /> : null}
        {render.isLoading ? (
          <p className="m-0 text-body text-text-secondary">Đang tải bản in…</p>
        ) : null}
        {render.isError ? (
          <Banner
            tone="danger"
            title={
              render.error instanceof Error
                ? render.error.message
                : "Không render được bản in."
            }
          />
        ) : null}
        {render.data?.html ? (
          <iframe
            ref={iframeRef}
            title={title}
            className="min-h-[min(70dvh,40rem)] w-full rounded-control border border-border bg-white"
            srcDoc={render.data.html}
            sandbox="allow-same-origin allow-modals"
          />
        ) : null}
      </div>
    </ContentModal>
  );
}
